import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Switch,
	FormControlLabel,
	Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import { actualizarUsuario } from '../../../services/apis';
import { AlertError } from '../../../utils/componentes-utiles/AlertError';
import { ContasenaOculta } from '../../../utils/componentes-utiles/ContasenaOculta';

export const EditarUsuario = ({
	open,
	setOpen,
	cargarUsuarios,
	roles,
	departamentos,
	usuarioData
}) => {

	const handleClose = () => setOpen(false);

	const [usuario, setUsuario] = useState({
		id: '',
		nombre: '',
		apellidos: '',
		mail: '',
		id_rol: '',
		id_departamento: '',
		color: '#1976d2',
		telefono: '',
		nif: ''
	});

	const [color, setColor] = useState('#1976d2');
	const [contrasena, setContrasena] = useState('');
	const [confirmarContrasena, setConfirmarContrasena] = useState('');
	const [cambiarContrasena, setCambiarContrasena] = useState(false);

	const [error, setError] = useState('');
	const [cargando, setCargando] = useState(false);

	// =========================
	// CARGA INICIAL
	// =========================
	useEffect(() => {
		if (usuarioData && open) {
			setUsuario({
				id: usuarioData.id,
				nombre: usuarioData.nombre || '',
				apellidos: usuarioData.apellidos || '',
				mail: usuarioData.mail || '',
				id_rol: usuarioData.rol || usuarioData.id_rol || '',
				id_departamento: usuarioData.departamento || usuarioData.id_departamento || '',
				color: usuarioData.color || '#1976d2',
				telefono: usuarioData.telefono || '',
				nif: usuarioData.nif || ''
			});
			setColor(usuarioData.color || '#1976d2');
			setCambiarContrasena(false);
			setContrasena('');
			setConfirmarContrasena('');
			setError('');
			setCargando(false);
		}
	}, [usuarioData, open]);

	// =========================
	// HANDLERS
	// =========================
	const handleChange = (e) => {
		setUsuario({ ...usuario, [e.target.name]: e.target.value });
		setError('');
	};

	const handleSubmit = async () => {
		if (!usuario.nombre || !usuario.mail || !usuario.id_rol) {
			setError('Nombre, email y rol son obligatorios');
			return;
		}

		if (cambiarContrasena && contrasena !== confirmarContrasena) {
			setError('Las contraseñas no coinciden');
			return;
		}

		const payload = {
			...usuario,
			color
		};

		if (cambiarContrasena && contrasena) {
			payload.contrasena = contrasena;
		}

		try {
			setCargando(true);
			const response = await actualizarUsuario(payload);

			if (response?.success) {
				cargarUsuarios();
				handleClose();
			} else {
				setError(response?.error || 'Error al actualizar usuario');
			}
		} catch {
			setError('Error de conexión con el servidor');
		} finally {
			setCargando(false);
		}
	};

	// =========================
	// RENDER
	// =========================
	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<Grid container justifyContent="space-between" alignItems="center">
				<DialogTitle>Editar Usuario</DialogTitle>
				<CancelIcon onClick={handleClose} style={{ cursor: 'pointer', marginRight: 16 }} />
			</Grid>

			<DialogContent>
				<Grid container spacing={2}>

					<Grid container justifyContent="center" size={{ xs: 12 }}>
						<AccountCircleIcon sx={{ fontSize: 90, color }} />
						<input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="Nombre" fullWidth name="nombre" value={usuario.nombre} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="Apellidos" fullWidth name="apellidos" value={usuario.apellidos} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12 }}>
						<TextField label="Email" fullWidth name="mail" value={usuario.mail} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="Teléfono" fullWidth name="telefono" value={usuario.telefono} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="NIF" fullWidth name="nif" value={usuario.nif} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12 }}>
						<FormControlLabel
							control={<Switch checked={cambiarContrasena} onChange={() => setCambiarContrasena(!cambiarContrasena)} />}
							label="Cambiar contraseña"
						/>
					</Grid>

					{cambiarContrasena && (
						<>
							<Grid item size={{ xs: 12, md: 6 }}>
								<ContasenaOculta contrasena={contrasena} setContrasena={setContrasena} />
							</Grid>
							<Grid item size={{ xs: 12, md: 6 }}>
								<ContasenaOculta contrasena={confirmarContrasena} setContrasena={setConfirmarContrasena} />
							</Grid>
						</>
					)}

					<Grid item size={{ xs: 12, md: 6 }}>
						<FormControl fullWidth>
							<InputLabel>Rol</InputLabel>
							<Select name="id_rol" value={usuario.id_rol} onChange={handleChange}>
								{roles.map(r => (
									<MenuItem key={r.id} value={r.id}>{r.nombre}</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<FormControl fullWidth>
							<InputLabel>Departamento</InputLabel>
							<Select name="id_departamento" value={usuario.id_departamento} onChange={handleChange}>
								<MenuItem value="">Sin departamento</MenuItem>
								{departamentos.map(d => (
									<MenuItem key={d.id} value={d.id}>{d.nombre}</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose}>Cancelar</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={cargando}>
					Guardar cambios
				</Button>
			</DialogActions>

			{error && (
				<AlertError open={true} setOpen={() => setError('')} titulo="Error" mensaje={error} />
			)}
		</Dialog>
	);
};
